import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { apiClient } from '../api';
import { DeleteButton } from './shared/DeleteButton';

interface TrustRegistry {
  id: string;
  name: string;
  description: string;
  did: string;
  status: 'active' | 'disabled';
  created_at: string;
  updated_at: string;
}

const TrustRegistriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [trustRegistries, setTrustRegistries] = useState<TrustRegistry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchTrustRegistries();
  }, []);

  // Auto-redirect to wizard if no trust registries exist
  useEffect(() => {
    if (!loading && trustRegistries.length === 0 && !error && hasPermission('trust_registries.edit')) {
      navigate('/trust-registries/wizard');
    }
  }, [loading, trustRegistries.length, error, hasPermission, navigate]);

  const fetchTrustRegistries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/trust-registries');
      setTrustRegistries(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to load trust registries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/trust-registries/${id}`);
      setSuccess('Trust registry deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchTrustRegistries();
    } catch (error: any) {
      setError(error.message || 'Failed to delete trust registry');
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
        {hasPermission('trust_registries.edit') && (
          <div>
            <button 
              className="btn btn-sm btn-primary" 
              onClick={() => navigate('/trust-registries/wizard')}
            >
              <i className="fas fa-plus me-2"></i> Add Trust Registry</button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : trustRegistries.length === 0 ? (
        <div className="card shadow mb-4">
          <div className="card-body text-center">
            <div className="text-muted">
              <i className="fas fa-shield-alt fa-3x mb-3"></i>
              <p>No trust registries found.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-shield-alt"></i> All Trust Registries
            </h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>DID</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trustRegistries.map((registry) => (
                    <tr key={registry.id}>
                      <td>{registry.name}</td>
                      <td><small>{registry.description}</small></td>
                      <td><code style={{fontSize: '0.75rem'}}>{registry.did}</code></td>
                      <td>
                        <span className={`badge ${registry.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
                          {registry.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        {hasPermission('trust_registries.edit') && (
                          <button
                            title="Edit Trust Registry"
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => navigate(`/trust-registries/${registry.id}`)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                        {hasPermission('trust_registries.delete') && (
                          <DeleteButton
                            onDelete={() => handleDelete(registry.id)}
                            className="btn-sm"
                            title="Delete Trust Registry"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustRegistriesPage;
